import Image from "next/image"
import { useRouter } from "next/router"
import { TransportIcon } from "../../components/svgs"
import stateCityDictionary, {
  formatName,
  urlifyName,
} from "../../constants/stateCityDictionary"
import * as config from "../../util/config"

type Props = {
  needForTransport: Record<string, { state: string; data: string }[]>
}

export default function PetTransportLaws(props: Props) {
  const { needForTransport } = props

  const router = useRouter()
  const { city, state } = router.query
  const host = config.getWebsiteHost()

  const formattedState = formatName(state as string)
  const urlifiedCity = city ? urlifyName(city as string) : ""

  const originalCity =
    stateCityDictionary[formattedState]?.cities.find(
      (dictCity) => urlifyName(dictCity) === urlifiedCity,
    ) ?? ""

  const formattedCity = originalCity ? formatName(originalCity) : ""

  const stateAbbrev = stateCityDictionary[formattedState]?.abbreviation ?? ""

  const locationName = formattedCity
    ? `${formattedCity}, ${stateAbbrev}`
    : formattedState

  const detectVowelBeginning = ["A", "E", "I", "O", "U"].includes(
    formattedCity[0],
  )
    ? "an"
    : "a"

  const addBulletPoints = (content: string) => {
    const pawIconSvg = `<svg class="min-w-[20px] w-5 mr-3 mt-1 fill-[#2563eb]/80" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5s.3-86.2 32.6-96.8s70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7 .9 78.5 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7c-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2C84.9 480 64 459.1 64 433.3v-1.6c0-10.4 1.6-20.8 5.2-30.5zM421.8 282.7c-24.5-14-29.1-51.7-10.2-84.1s54-47.3 78.5-33.3s29.1 51.7 10.2 84.1s-54 47.3-78.5 33.3zM310.1 189.7c-32.3-10.6-46.9-53.9-32.6-96.8s52.1-69.1 84.4-58.5s46.9 53.9 32.6 96.8s-52.1 69.1-84.4 58.5z"/></svg>`
    return content.replace(
      /<li>/g,
      `<li class="flex items-start">${pawIconSvg}`,
    )
  }

  const key =
    needForTransport[state as string] ||
    needForTransport[city as string] ||
    undefined

  return (
    <section className="w-full my-16 xl:my-32 flex flex-col items-center lg:flex-row">
      <Image
        className="hidden lg:block"
        src="/images/pet-laws.webp"
        alt={`pet shipping ${locationName}`}
        width={300}
        height={630}
      />
      <article className="lg:pt-0 lg:pr-0 lg:pb-0 lg:pl-32">
        <h3 className="text-blue7 text-3xl font-medium font-montserrat mb-5">
          What do you need for pet shipping to or from {locationName}?
        </h3>
        {key ? (
          <div
            className="prose-headings:font-montserrat prose-h4:text-lg prose-h4:text-blue7 prose-h4:font-normal prose-p:text-xl prose-p:m-0 prose-p:py-1.5 prose-ul:mt-6 prose-ul:p-0 prose-ul:list-none prose-li:mb-1 prose-li:text-lg prose-li:leading-normal prose-li:pl-4"
            dangerouslySetInnerHTML={{
              __html: addBulletPoints(key[0].data),
            }}
          ></div>
        ) : (
          <div>
            <figure className="relative">
              <Image
                className="float-right lg:hidden h-64 w-auto"
                src="/images/pet-laws.webp"
                alt={`pet shipping ${locationName}`}
                width={300}
                height={630}
              />
              <figcaption>
                <p className="text-xl font-roboto ">
                  Before you hire {detectVowelBeginning}{" "}
                  {formattedCity ? formattedCity : formattedState}{" "}
                  <a href={`${host}/pet-transportation`}>pet transport</a>{" "}
                  company, there are a few important steps. These tasks comply
                  with local pet transportation law, ensure your dog or cat is
                  healthy enough for the trip and arrives at its destination
                  safely.
                </p>
                <p className="text-xl font-roboto ">
                  First, obtain a Certificate of Veterinary Inspection from a
                  licensed and certified veterinarian in your city. This
                  document must be issued within 10 days of pet travel.
                </p>
                <p className="text-xl font-roboto ">
                  Next, your pet needs a signed rabies vaccination health
                  certificate. This must be issued within 30 days of the pet
                  transport.
                </p>
                <p className="text-xl font-roboto ">
                  Some pet transportation companies in {locationName} provide
                  their own pet carriers. If they don't, you'll need to purchase
                  one. The pet carrier you purchase should meet the following
                  standards:
                </p>
              </figcaption>
            </figure>
            <ul className="p-0">
              <li className="mb-1 flex items-start pl-4">
                <svg
                  className="min-w-[20px] w-5 mr-3 mt-1 fill-blue7/80"
                  viewBox="0 0 512 512"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5s.3-86.2 32.6-96.8s70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7 .9 78.5 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7c-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2C84.9 480 64 459.1 64 433.3v-1.6c0-10.4 1.6-20.8 5.2-30.5zM421.8 282.7c-24.5-14-29.1-51.7-10.2-84.1s54-47.3 78.5-33.3s29.1 51.7 10.2 84.1s-54 47.3-78.5 33.3zM310.1 189.7c-32.3-10.6-46.9-53.9-32.6-96.8s52.1-69.1 84.4-58.5s46.9 53.9 32.6 96.8s-52.1 69.1-84.4 58.5z" />
                </svg>
                <span className="leading-normal">
                  The animal should be able to stand, sit and turn around in the
                  carrier.
                </span>
              </li>
              <li className="mb-1 flex items-start pl-4">
                <svg
                  className="min-w-[20px] w-5 mr-3 mt-1 fill-blue7/80"
                  viewBox="0 0 512 512"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5s.3-86.2 32.6-96.8s70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7 .9 78.5 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7c-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2C84.9 480 64 459.1 64 433.3v-1.6c0-10.4 1.6-20.8 5.2-30.5zM421.8 282.7c-24.5-14-29.1-51.7-10.2-84.1s54-47.3 78.5-33.3s29.1 51.7 10.2 84.1s-54 47.3-78.5 33.3zM310.1 189.7c-32.3-10.6-46.9-53.9-32.6-96.8s52.1-69.1 84.4-58.5s46.9 53.9 32.6 96.8s-52.1 69.1-84.4 58.5z" />
                </svg>
                <span className="leading-normal">
                  The carrier needs adequate ventilation and a solid bottom in
                  case of an accident. This will help your pet stay calm and not
                  stress out.
                </span>
              </li>
              <li className="mb-1 flex items-start pl-4">
                <svg
                  className="min-w-[20px] w-5 mr-3 mt-1 fill-blue7/80"
                  viewBox="0 0 512 512"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5s.3-86.2 32.6-96.8s70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7 .9 78.5 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7c-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2C84.9 480 64 459.1 64 433.3v-1.6c0-10.4 1.6-20.8 5.2-30.5zM421.8 282.7c-24.5-14-29.1-51.7-10.2-84.1s54-47.3 78.5-33.3s29.1 51.7 10.2 84.1s-54 47.3-78.5 33.3zM310.1 189.7c-32.3-10.6-46.9-53.9-32.6-96.8s52.1-69.1 84.4-58.5s46.9 53.9 32.6 96.8s-52.1 69.1-84.4 58.5z" />
                </svg>
                <span className="leading-normal">
                  The door must close securely.
                </span>
              </li>
              <li className="mb-1 flex items-start pl-4">
                <svg
                  className="min-w-[20px] w-5 mr-3 mt-1 fill-blue7/80"
                  viewBox="0 0 512 512"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5s.3-86.2 32.6-96.8s70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7 .9 78.5 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7c-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2C84.9 480 64 459.1 64 433.3v-1.6c0-10.4 1.6-20.8 5.2-30.5zM421.8 282.7c-24.5-14-29.1-51.7-10.2-84.1s54-47.3 78.5-33.3s29.1 51.7 10.2 84.1s-54 47.3-78.5 33.3zM310.1 189.7c-32.3-10.6-46.9-53.9-32.6-96.8s52.1-69.1 84.4-58.5s46.9 53.9 32.6 96.8s-52.1 69.1-84.4 58.5z" />
                </svg>
                <span className="leading-normal">
                  The crate must have padded walls if you're transporting
                  puppies or kittens so they feel safe and comfortable.
                </span>
              </li>
              <li className="mb-1 flex items-start pl-4">
                <svg
                  className="min-w-[20px] w-5 mr-3 mt-1 fill-blue7/80"
                  viewBox="0 0 512 512"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5s.3-86.2 32.6-96.8s70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7 .9 78.5 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7c-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2C84.9 480 64 459.1 64 433.3v-1.6c0-10.4 1.6-20.8 5.2-30.5zM421.8 282.7c-24.5-14-29.1-51.7-10.2-84.1s54-47.3 78.5-33.3s29.1 51.7 10.2 84.1s-54 47.3-78.5 33.3zM310.1 189.7c-32.3-10.6-46.9-53.9-32.6-96.8s52.1-69.1 84.4-58.5s46.9 53.9 32.6 96.8s-52.1 69.1-84.4 58.5z" />
                </svg>
                <span className="leading-normal">
                  If boarding a plane, ensure it meets airline pet carrier size
                  requirements.
                </span>
              </li>
            </ul>
          </div>
        )}
      </article>
    </section>
  )
}
